import { action, flow, makeObservable } from 'mobx';
import moment from 'moment';

import { hasValue } from 'src/shared/utils/common';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import type { RootStore } from 'src/store';

import type { FormStore } from '../../entities/form.entity';
import { FormPlugin } from '../abstract-form-plugin.entity';

export class PassingValueFormulaPlugin extends FormPlugin<FormStore> {
  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this);
  }

  @flow.bound
  private async *loadPassingValueBySectionsDirectory() {
    if (!this.form) {
      return;
    }

    const { rigOperationsService } = this.form;
    // const { block, field, fluidType, wellType, wellPurpose, reservoir } = this.form.fields;
    const { block, field, fluidType, wellType, wellPurpose } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value) {
      return;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    await this.rootStore.directories.loadObjects([
      [
        'Common_WellPassingBySection',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          wellPurposeId: wellPurpose.value,
          // reservoirIds: reservoir.value,
          year,
        },
      ],
    ]);
    yield;
  }

  @flow.bound
  private async *loadWellPassingValueDirectory() {
    if (!this.form) {
      return;
    }

    const { rigOperationsService } = this.form;
    // const { block, field, fluidType, wellType, reservoir } = this.form.fields;
    const { block, field, fluidType, wellType } = this.form.fields;

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value) {
      return;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    await this.rootStore.directories.loadObjects([
      [
        'Common_WellPassing',
        {
          blockId: block.value,
          fieldId: field.value,
          fluidTypeId: fluidType.value,
          wellTypeId: wellType.value,
          // reservoirIds: reservoir.value,
          year,
        },
      ],
    ]);
    yield;
  }

  @action.bound
  private calculateGeoTaskPassingValueByDirectoryValues(): number | null {
    if (!this.form) {
      return null;
    }

    const { rigOperationsService } = this.form;
    const {
      block,
      field,
      fluidType,
      wellType,
      wellPurpose,
      reservoir,
      trunkLength: trunkLengthControl,
      pilotNumber: pilotControl,
    } = this.form.fields;

    if (!trunkLengthControl) {
      console.error('trunkLengthControl is not presented');
      return null;
    }

    if (!block?.value || !field?.value || !fluidType?.value || !wellType?.value || !wellPurpose?.value) {
      return null;
    }

    const year = (() => {
      const stageStartDate = rigOperationsService.rigOperations[0]?.stages.find((s) =>
        rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
      )?.startDate;

      return stageStartDate ? moment(stageStartDate * 1000).year() : moment().year();
    })();

    const wellPassingFilterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      // reservoirIds: reservoir.value,
      year,
    };

    const wellPassingBySectionsFilterData = {
      blockId: block.value,
      fieldId: field.value,
      fluidTypeId: fluidType.value,
      wellTypeId: wellType.value,
      wellPurposeId: wellPurpose.value,
      // reservoirIds: reservoir.value,
      year,
    };

    const wellPassingDirectories = this.rootStore.directories
      .getObject('Common_WellPassing', true, wellPassingFilterData)
      ?.filter((dv) => {
        const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
        const reservoirIds = reservoir.value as number[];

        if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
          return false;
        }

        return reservoirIds.every((v) => dvReservoirIds.includes(v));
      });

    if (!wellPassingDirectories || !wellPassingDirectories.length) {
      return null;
    }

    if (wellPassingDirectories.length > 1) {
      console.error(
        `Found multiple active Common_WellPassing directories.
         Automatic passing value calculation will be disabled until this issue is fixed.`,
      );
      return null;
    }

    const wellPassingDirectory = wellPassingDirectories[0];

    const passingByWellPurpose = wellPassingDirectory.data['passingByWellPurpose'];

    if (!Array.isArray(passingByWellPurpose)) {
      console.error('passingByWellPurpose is invalid');
      return null;
    }

    const baseWellPassing = passingByWellPurpose.find(
      (passingObject) => parsePrimitivesFromString(passingObject['wellPurposeId']) === wellPurpose.value,
    )?.['passing'];

    if (!hasValue(baseWellPassing) || typeof baseWellPassing !== 'number') {
      return null;
    }

    const trunkLength =
      hasValue(trunkLengthControl.value) && typeof trunkLengthControl.value === 'number' ? trunkLengthControl.value : 0;

    const extraPassingByPilots = ((): number => {
      if (!pilotControl || !hasValue(pilotControl.value) || typeof pilotControl.value !== 'number') {
        return 0;
      }

      const pilotsCount = pilotControl.value;

      const wellPassingBySectionsDirectories = this.rootStore.directories
        .getObject('Common_WellPassingBySection', true, wellPassingBySectionsFilterData)
        ?.filter((dv) => {
          const dvReservoirIds = dv.data['reservoirIds'] as unknown as Array<number>;
          const reservoirIds = reservoir.value as number[];

          if ((dvReservoirIds?.length ?? 0) !== (reservoirIds.length ?? 0)) {
            return false;
          }

          return reservoirIds.every((v) => dvReservoirIds.includes(v));
        });

      if (!wellPassingBySectionsDirectories || !wellPassingBySectionsDirectories.length) {
        return 0;
      }

      if (wellPassingBySectionsDirectories.length > 1) {
        console.error(
          `Found multiple active Common_WellPassingBySection directories.
           Data on pilots will not be included in the calculation.`,
        );
        return 0;
      }

      const wellPassingBySectionsDirectory = wellPassingBySectionsDirectories[0];

      const appSettingsPilotSectionIdDirectory = this.rootStore.directories
        .getObject('GOplan_AppSettings')
        ?.find((dv) => dv.data['key'] === 'pilotSectionId');

      if (!appSettingsPilotSectionIdDirectory) {
        console.error(
          `cannot find app settings directory with sectionTypeId key.
           Data on pilots will not be included in the calculation.`,
        );
        return 0;
      }

      const pilotSectionId = appSettingsPilotSectionIdDirectory.data['value'];

      if (typeof pilotSectionId !== 'number') {
        console.error(
          'invalid pilotSectionId value. Data on pilots will not be included in the calculation.',
          pilotSectionId,
        );
        return 0;
      }

      const passingBySectionType = wellPassingBySectionsDirectory.data['passingBySection'];

      if (!Array.isArray(passingBySectionType)) {
        console.error('passingBySectionType is invalid');
        return 0;
      }

      const pilotSectionBasePassing = passingBySectionType.find(
        (passingObject) => parsePrimitivesFromString(passingObject['sectionTypeId']) === pilotSectionId,
      )?.['passing'];

      if (!hasValue(pilotSectionBasePassing) || typeof pilotSectionBasePassing !== 'number') {
        return 0;
      }

      return pilotsCount * pilotSectionBasePassing;
    })();

    return baseWellPassing + trunkLength + extraPassingByPilots;
  }

  @action.bound
  private calculateGeoTaskPassingValue() {
    if (!this.form) {
      console.error('from is not presented');
      return;
    }

    const { rigOperationsService, fields, onValueChange } = this.form;

    const mainDataPassingValueControl = fields['passingValue'];

    if (!mainDataPassingValueControl) {
      console.error('cannot find passingValue control');
      return;
    }

    const rigOperationsPassingValue = rigOperationsService.rigOperations.reduce(
      (acc: number | null, currRigOperation) => {
        const rigOperationPassingValue = currRigOperation.stages.reduce((acc: number | null, currStage) => {
          if (rigOperationsService.hasSections(currStage.wellLifeCycleStageId)) {
            if (hasValue(acc)) {
              return acc + (currStage.passingValue ?? 0);
            } else {
              return currStage.passingValue;
            }
          }

          return acc;
        }, null);

        if (hasValue(acc)) {
          return acc + (rigOperationPassingValue ?? 0);
        } else {
          return rigOperationPassingValue;
        }
      },
      null,
    );

    if (rigOperationsService.isSectionDetailingOrManyRigOperations) {
      onValueChange('passingValue', rigOperationsPassingValue, { control: mainDataPassingValueControl });
      return;
    }

    const passingValue = this.calculateGeoTaskPassingValueByDirectoryValues();
    onValueChange('passingValue', passingValue, { control: mainDataPassingValueControl });
  }

  // Пока что отключено
  // @flow.bound
  // async *loadPassingValueDirectoryValues() {
  //   if (!this.form) {
  //     console.error('form is not presented');
  //     return;
  //   }

  //   try {
  //     const data = await getDirectoryValues(this.form, ['passingValue']);
  //     console.log(data);
  //   } catch (e) {
  //     yield;
  //     console.error(e);
  //   }
  // }

  @action.bound
  connect(form: FormStore): VoidFunction {
    this.form = form;

    const updatedRigOperationsListDisposer = form.eventBus.subscribe(
      'updatedRigOperaionList',
      this.calculateGeoTaskPassingValue.bind(this),
    );
    const updatedStagesListDisposer = form.eventBus.subscribe(
      'updatedStageList',
      this.calculateGeoTaskPassingValue.bind(this),
    );
    const stagePassingValueChangeDisposer = form.eventBus.subscribe(
      'rigOperationItemFieldValueChange',
      ({ fieldId, section }) => {
        if (fieldId === 'passingValue' && !section) {
          this.calculateGeoTaskPassingValue();
        }
      },
    );
    const deletedRigOperationDisposer = form.eventBus.subscribe(
      'deleteRigOperation',
      this.calculateGeoTaskPassingValue.bind(this),
    );
    const deletedStageDisposer = form.eventBus.subscribe('deleteStage', this.calculateGeoTaskPassingValue.bind(this));
    const trunkLengthValueChangeDisposer = form.eventBus.subscribe('mainDataValueChange', ({ fieldId }) => {
      if (!['trunkLength', 'miniPilotNumber', 'pilotNumber'].includes(fieldId)) {
        return;
      }

      this.calculateGeoTaskPassingValue();
    });

    const directoriesValuesDisposer = (() => {
      const mainDataFieldsDisposer = form.eventBus.subscribe('mainDataValueChange', async ({ fieldId }) => {
        // TODO: при рефакторинге основной вкладки учесть, что здесь pad нужно будет заменить на field и block, т.к. текущее решение - костыль
        if (!['pad', 'fluidType', 'wellType', 'wellPurpose', 'reservoir'].includes(fieldId)) {
          return;
        }

        const requests: Promise<unknown>[] = [];

        // Пока что отключено
        // await this.loadPassingValueDirectoryValues();

        if (fieldId !== 'wellPurpose') {
          requests.push(this.loadWellPassingValueDirectory());
        }

        requests.push(this.loadPassingValueBySectionsDirectory());

        await Promise.all(requests);

        this.calculateGeoTaskPassingValue();
      });
      const drillingStageStartDateDisposer = form.eventBus.subscribe(
        'rigOperationItemFieldValueChange',
        async ({ fieldId, stage }) => {
          const { rigOperations } = form.rigOperationsService;

          if (
            fieldId !== 'startDate' ||
            !stage ||
            stage.sections.length ||
            rigOperations.length > 1 ||
            !form.rigOperationsService.isDrillingStage(stage.wellLifeCycleStageId)
          ) {
            return;
          }

          await Promise.all([this.loadWellPassingValueDirectory(), this.loadPassingValueBySectionsDirectory()]);

          this.calculateGeoTaskPassingValue();
        },
      );
      const deleteRigOperationDisposer = form.eventBus.subscribe('deleteRigOperation', async () => {
        const { rigOperations } = form.rigOperationsService;

        if (rigOperations.length > 1 || !rigOperations.length || !rigOperations[0]?.stages.length) {
          return;
        }

        const drillingStage = rigOperations[0]?.stages.find((s) =>
          form.rigOperationsService.isDrillingStage(s.wellLifeCycleStageId),
        );

        if (!drillingStage || !drillingStage.startDate) {
          return;
        }

        await Promise.all([this.loadWellPassingValueDirectory(), this.loadPassingValueBySectionsDirectory()]);

        this.calculateGeoTaskPassingValue();
      });

      return () => {
        mainDataFieldsDisposer();
        drillingStageStartDateDisposer();
        deleteRigOperationDisposer();
      };
    })();

    return () => {
      updatedRigOperationsListDisposer();
      updatedStagesListDisposer();
      stagePassingValueChangeDisposer();
      deletedRigOperationDisposer();
      deletedStageDisposer();
      trunkLengthValueChangeDisposer();
      directoriesValuesDisposer();
    };
  }
}
